import _ from 'lodash'
import { Component, MeshContainerLayout, MeshItemLayout, SingleLayoutData } from '@wix/thunderbolt-becky-types'
import { MESH_ID_SEPARATOR } from '../meshConstants'
import { MeshResult } from '../../../types/css'
import { getMesh3Data, getMeshComponents, getMeshData, getStylesAndWedgesAndRotated } from '../meshUtils'
import { BeckyFunctionLibrary, ChildrenGroup, SoapGroupsToChildrenLayoutData } from '../../../types'
import { SoapGroupsToChildrenRotation } from '../../../types/mesh'

const emptyMeshResults = { stylesMap: {}, wedges: [], rotatedComponents: [] } as MeshResult

const getChildrenWithoutSOSP = (childComponents: Array<Component>): Array<Component> =>
	childComponents.filter((comp) => {
		const hasModes =
			comp.modes && ((comp.modes.overrides && !_.isEmpty(comp.modes.overrides)) || comp.modes.isHiddenByModes)
		return comp.componentType === 'HoverBox' || !hasModes
	})

export const getMeshResultsForMasterPage: BeckyFunctionLibrary['getMeshResultsForMasterPage'] = (
	compMeshData,
	overridesData,
	absoluteComps,
	autoHeightComps
) => {
	const masterPageChildrenGroups = _.get(compMeshData, 'dataForMasterPage.childrenGroups', []) as Array<ChildrenGroup>
	const soapGroupsToChildrenSingleLayoutData = _.get(
		compMeshData,
		'dataForMasterPage.soapGroupsToChildrenSingleLayoutData',
		{}
	) as SoapGroupsToChildrenLayoutData
	const soapGroupsToChildrenRotation = _.get(
		compMeshData,
		'dataForMasterPage.soapGroupsToChildrenRotation',
		{}
	) as SoapGroupsToChildrenRotation
	const isOneDocMigrated = compMeshData.isOneDocMigrated

	const getMasterPagePartMeshResult = (
		compId: string,
		childComponents: Array<Component>,
		withSOSPComps: boolean
	): MeshResult => {
		const childrenAfterSOSP = withSOSPComps ? childComponents : getChildrenWithoutSOSP(childComponents)
		const childrenIds = childrenAfterSOSP.map((comp) => comp.id)
		const compStructure = {
			id: compId,
			components: childrenIds,
			componentType: 'SoapContainer',
			layout: {},
			type: 'Container',
			parent: null,
			styleId: '',
			metaData: {},
		} as Component
		const structure = {
			..._.keyBy(childrenAfterSOSP, (comp) => comp.id),
			[compStructure.id]: compStructure,
		}

		let meshData

		if (isOneDocMigrated) {
			const compToSingleLayoutData = _.pick(soapGroupsToChildrenSingleLayoutData[compId], childrenIds)
			const childrenMap = _.mapValues(compToSingleLayoutData, (comp) => {
				const itemLayout = comp.itemLayout as MeshItemLayout
				if (itemLayout.isAbsolutePosition) {
					return comp
				}

				return {
					...comp,
					itemLayout: {
						...itemLayout,
						meshData: {
							...itemLayout.meshData,
							y: itemLayout.meshData.y - overridesData.soapTopOffset,
						},
					},
				}
			})
			meshData = getMesh3Data(
				compStructure.id,
				{
					type: 'SingleLayoutData',
					containerLayout: {
						type: 'MeshContainerLayout',
						meshData: {
							contentHeight: 'auto',
						},
						contentArea: {
							alignment: 'center',
							width: {
								value: 'siteWidth',
								type: 'SystemVariable',
							},
						},
					} as MeshContainerLayout,
				} as SingleLayoutData,
				childrenMap,
				soapGroupsToChildrenRotation[compId]
			)
		} else {
			const comps = getMeshComponents(compId, structure, absoluteComps)
			const nonAbsComps = _.reject(comps, 'absolute')
			const compsWithHeaderOffset = comps.map((comp) => {
				const shouldSubtractHeaderBottom = nonAbsComps.length === 0 && comp.absolute
				const top = shouldSubtractHeaderBottom ? comp.top : comp.top - overridesData.soapTopOffset

				return { ...comp, top }
			})
			meshData = getMeshData(
				{ compStructure, compData: {}, isOneDocMigrated },
				compsWithHeaderOffset,
				overridesData
			)
		}
		meshData.styles = withSOSPComps
			? _.mapKeys(meshData.styles, (val, key) => `${key}${MESH_ID_SEPARATOR}page-with-sosp`)
			: _.mapKeys(meshData.styles, (val, key) => `${key}${MESH_ID_SEPARATOR}page-without-sosp`)

		const meshResults = getStylesAndWedgesAndRotated(compId, meshData, autoHeightComps, {
			isMeshCatharsisExperimentOpen: compMeshData.isMeshCatharsisExperimentOpen,
		})

		return childrenIds.length ? meshResults : emptyMeshResults
	}

	const soapBefore = masterPageChildrenGroups.find((group) => group.id === 'soapBeforePagesContainer')
	const soapAfter = masterPageChildrenGroups.find((group) => group.id === 'soapAfterPagesContainer')

	const soapBeforeComps = (soapBefore && soapBefore.components) || []
	const soapAfterComps = (soapAfter && soapAfter.components) || []

	const soapBeforePagesContainerResults = getMasterPagePartMeshResult(
		'soapBeforePagesContainer',
		soapBeforeComps,
		false
	)
	const soapAfterPagesContainerResults = getMasterPagePartMeshResult('soapAfterPagesContainer', soapAfterComps, false)

	const soapBeforePagesContainerResultsWithSOSP = getMasterPagePartMeshResult(
		'soapBeforePagesContainer',
		soapBeforeComps,
		true
	)
	const soapAfterPagesContainerResultsWithSOSP = getMasterPagePartMeshResult(
		'soapAfterPagesContainer',
		soapAfterComps,
		true
	)

	// Adding a repeater styles to mesh so we will have it in the final css
	let combinedStyles = {}
	let combinedRepeaterStyles = {}

	combinedStyles = {
		...(soapBeforePagesContainerResults.stylesMap?.stylesMap || soapBeforePagesContainerResults.stylesMap),
		...(soapBeforePagesContainerResultsWithSOSP.stylesMap?.stylesMap ||
			soapBeforePagesContainerResultsWithSOSP.stylesMap),
		...(soapAfterPagesContainerResults.stylesMap?.stylesMap || soapAfterPagesContainerResults.stylesMap),
		...(soapAfterPagesContainerResultsWithSOSP.stylesMap?.stylesMap ||
			soapAfterPagesContainerResultsWithSOSP.stylesMap),
	}

	combinedRepeaterStyles = {
		...soapBeforePagesContainerResults.repeaterStylesMap,
		...soapBeforePagesContainerResultsWithSOSP.repeaterStylesMap,
		...soapAfterPagesContainerResults.repeaterStylesMap,
		...soapAfterPagesContainerResultsWithSOSP.repeaterStylesMap,
	}

	const regularModeRotated = soapBeforePagesContainerResults.rotatedComponents.concat(
		soapAfterPagesContainerResults.rotatedComponents
	)
	const sospModeRotated = soapBeforePagesContainerResultsWithSOSP.rotatedComponents.concat(
		soapAfterPagesContainerResultsWithSOSP.rotatedComponents
	)

	const regularModeWedges = soapBeforePagesContainerResults.wedges.concat(soapAfterPagesContainerResults.wedges)
	const sospModeWedges = soapBeforePagesContainerResultsWithSOSP.wedges.concat(
		soapAfterPagesContainerResultsWithSOSP.wedges
	)

	return {
		stylesMap: combinedStyles,
		repeaterStylesMap: combinedRepeaterStyles,
		rotatedComponents: _.uniq(regularModeRotated.concat(sospModeRotated)),
		wedges: _.uniq(regularModeWedges.concat(sospModeWedges)),
	}
}
